import { useState } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  LogOut,
  LayoutGrid,
  Ship,
  Factory,
  CalendarDays,
  Video,
  Users,
  Building2,
  ChevronRight,
  History,
  Library,
  Sparkles,
  Brain,
  Rocket,
} from "lucide-react";
import { useAuth } from "@workos-inc/authkit-react";
import { cn } from "@/lib/utils";
import { getTenantIdentity } from "@/lib/tenant";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { usePeople, useVendors } from "../pages/flameking/use-directory";
import { flamekingLogoUrl } from "./flameking-logo";

type NavItem = {
  label: string;
  to: string;
  icon: typeof LayoutDashboard;
  pattern: string;
};

type NavGroup = {
  key: string;
  label: string;
  to: string;
  icon: typeof LayoutDashboard;
  pattern: string;
  childPattern: string;
  children: { label: string; to: string }[];
};

const navItemsHead: NavItem[] = [
  { label: "Operations", to: "/operations", icon: LayoutGrid, pattern: "/operations" },
  { label: "Freight", to: "/freight", icon: Ship, pattern: "/freight" },
  { label: "FlameKing Plan", to: "/production-plan", icon: Factory, pattern: "/production-plan" },
  { label: "Calendar", to: "/calendar", icon: CalendarDays, pattern: "/calendar" },
  { label: "Meetings", to: "/meetings", icon: Video, pattern: "/meetings" },
];

const navItemsTail: NavItem[] = [
  { label: "Memory", to: "/memory", icon: Brain, pattern: "/memory" },
  { label: "Knowledge Base", to: "/knowledge-base", icon: Library, pattern: "/knowledge-base" },
  { label: "Skills", to: "/skills", icon: Sparkles, pattern: "/skills" },
  { label: "Skills Board", to: "/skills-board", icon: Sparkles, pattern: "/skills-board" },
  { label: "Update Log", to: "/update-log", icon: History, pattern: "/update-log" },
  { label: "TM Board", to: "/tm-board", icon: Rocket, pattern: "/tm-board" },
];

function isActive(pattern: string, pathname: string) {
  return !!matchPath({ path: pattern, end: true }, pathname);
}

function FlatLink({ item }: { item: NavItem }) {
  const location = useLocation();
  const active =
    isActive(item.pattern, location.pathname) ||
    (item.pattern !== "/" && location.pathname === item.pattern);

  return (
    <Link
      to={item.to}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
        active
          ? "bg-accent text-foreground font-medium"
          : "text-muted-foreground hover:bg-accent hover:text-foreground",
      )}
    >
      <item.icon className="h-[18px] w-[18px]" />
      <span>{item.label}</span>
    </Link>
  );
}

function GroupSection({ group }: { group: NavGroup }) {
  const location = useLocation();
  const onParent = isActive(group.pattern, location.pathname);
  const onChild = !!matchPath({ path: group.childPattern, end: false }, location.pathname);
  const [open, setOpen] = useState<boolean>(onParent || onChild);

  return (
    <div>
      <div className="flex items-stretch">
        <Link
          to={group.to}
          className={cn(
            "flex flex-1 items-center gap-3 rounded-l-lg px-3 py-2 text-sm transition-colors",
            onParent
              ? "bg-accent text-foreground font-medium"
              : "text-muted-foreground hover:bg-accent hover:text-foreground",
          )}
        >
          <group.icon className="h-[18px] w-[18px]" />
          <span>{group.label}</span>
        </Link>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? `Collapse ${group.label}` : `Expand ${group.label}`}
          className={cn(
            "px-2 rounded-r-lg transition-colors",
            "text-muted-foreground hover:bg-accent hover:text-foreground",
          )}
        >
          <ChevronRight
            className={cn("h-4 w-4 transition-transform", open ? "rotate-90" : "rotate-0")}
          />
        </button>
      </div>

      {open && (
        <div className="mt-0.5 ml-7 flex flex-col gap-0.5 border-l border-border/60">
          {group.children.map((c) => {
            const childActive = location.pathname === c.to;
            return (
              <Link
                key={c.to}
                to={c.to}
                className={cn(
                  "flex items-center gap-2 rounded-lg pl-3 pr-2 py-1.5 text-xs transition-colors",
                  childActive
                    ? "bg-accent text-foreground font-medium"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
              >
                <span className="truncate">{c.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CrmSidebar() {
  const { user, signOut } = useAuth();
  const { displayName: tenantDisplayName } = getTenantIdentity();
  const peopleQ = usePeople();
  const vendorsQ = useVendors();

  const navGroups: NavGroup[] = [
    {
      key: "people",
      label: "People",
      to: "/people",
      icon: Users,
      pattern: "/people",
      childPattern: "/people/:name",
      children: (peopleQ.data ?? []).map((p) => ({
        label: p.name,
        to: `/people/${encodeURIComponent(p.name)}`,
      })),
    },
    {
      key: "vendors",
      label: "Vendors",
      to: "/vendors",
      icon: Building2,
      pattern: "/vendors",
      childPattern: "/vendors/:name",
      children: (vendorsQ.data ?? []).map((v) => ({
        label: v.name,
        to: `/vendors/${encodeURIComponent(v.name)}`,
      })),
    },
  ];

  const fullName = [user?.firstName, user?.lastName]
    .filter((v): v is string => Boolean(v))
    .join(" ");
  const displayName = fullName || user?.email || "User";
  const avatarUrl = user?.profilePictureUrl ?? undefined;
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <aside className="flex h-svh w-56 shrink-0 flex-col border-r bg-sidebar">
      <div className="px-3 py-3">
        <Link to="/" className="flex flex-col items-center gap-2">
          <img
            src={flamekingLogoUrl}
            alt={`${tenantDisplayName} logo`}
            className="block h-auto w-full max-w-[176px]"
          />
          <span className="text-sm font-semibold">Dashboard</span>
        </Link>
      </div>

      <nav className="flex flex-1 flex-col gap-0.5 px-2 overflow-y-auto pb-4">
        {navItemsHead.map((item) => (
          <FlatLink key={item.to} item={item} />
        ))}
        {navGroups.map((group) => (
          <GroupSection key={group.key} group={group} />
        ))}
        {navItemsTail.map((item) => (
          <FlatLink key={item.to} item={item} />
        ))}
      </nav>

      <div className="px-2 pb-3 pt-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
              <Avatar className="h-7 w-7">
                {avatarUrl && <AvatarImage src={avatarUrl} />}
                <AvatarFallback className="text-xs">{initial}</AvatarFallback>
              </Avatar>
              <span className="truncate">{displayName}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-48">
            <DropdownMenuItem
              onClick={() => signOut()}
              className="cursor-pointer"
            >
              <LogOut className="h-4 w-4 me-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
